import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Ticket, Upload, X, FileText, Image, Clock, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/api/api';
import { formatDate } from '../../utils/dateTimeUtils';

const CreateTicket = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    priority: 'medium',
    description: '',
    attachment_url: ''
  });
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checkingActive, setCheckingActive] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [stats, setStats] = useState({ open: 0, in_progress: 0, resolved: 0 });
  const [recentTickets, setRecentTickets] = useState([]);

  const MAX_FILES = 4;
  const MAX_FILE_SIZE = 150 * 1024; // 150KB
  const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];

  useEffect(() => {
    checkActiveTicket();
    fetchMyTickets();
  }, []);

  const checkActiveTicket = async () => {
    setCheckingActive(true);
    try {
      const response = await api.get('/tickets/active-check');
      const data = response.data;
      if (data.has_active && data.active_ticket) {
        setActiveTicket(data.active_ticket);
      }
    } catch (error) {
      console.error('Error checking active ticket:', error);
    } finally {
      setCheckingActive(false);
    }
  };

  const fetchMyTickets = async () => {
    try {
      const response = await api.get('/tickets/my-tickets');
      const ticketsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setRecentTickets(ticketsData.slice(0, 5));
      
      // Calculate stats
      const open = ticketsData.filter(t => t.status === 'open').length;
      const inProgress = ticketsData.filter(t => t.status === 'in_progress').length;
      const resolved = ticketsData.filter(t => t.status === 'resolved').length;
      setStats({ open, in_progress: inProgress, resolved });
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        attachments: attachments.map(file => ({
          filename: file.name,
          data: file.data,
          size: file.size,
          type: file.type
        }))
      };
      
      const response = await api.post('/tickets/', submitData);
      alert(`Ticket created successfully!\nTicket Number: ${response.data.ticket_number}`);
      setFormData({
        title: '',
        category: '',
        priority: 'medium',
        description: '',
        attachment_url: ''
      });
      setAttachments([]);
      fetchMyTickets();
      checkActiveTicket();
    } catch (error) {
      console.error('Error creating ticket:', error);
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail;
        if (detail?.active_ticket) {
          setActiveTicket(detail.active_ticket);
        }
        alert(detail?.message || 'You already have an active ticket. Please wait for it to be resolved.');
      } else {
        alert('Failed to create ticket. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed limit
    if (attachments.length + files.length > MAX_FILES) {
      alert(`Maximum ${MAX_FILES} files allowed. You can add ${MAX_FILES - attachments.length} more.`);
      return;
    }
    
    files.forEach(async (file) => {
      // Validate file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`Invalid file type: ${file.name}. Only PDF, JPG, and PNG files are allowed.`);
        return;
      }
      
      // Validate file size (max 5MB raw — auto-compressed if image)
      if (file.size > 5 * 1024 * 1024) {
        alert(`File too large: ${file.name}. Maximum size is 5MB.`);
        return;
      }
      
      // Smart read: auto-compresses images (screenshots) to ~150KB
      try {
        const { readFileSmart } = await import('@/utils/imageCompressor');
        const result = await readFileSmart(file);
        setAttachments(prev => [...prev, {
          name: file.name,
          data: result.data,
          size: result.size,
          type: file.type
        }]);
      } catch (err) {
        console.error('File read failed:', err);
        alert(`Failed to read ${file.name}`);
      }
    });
    
    // Clear input
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    return (bytes / 1024).toFixed(1) + ' KB';
  };

  const getFileIcon = (type) => {
    if (type === 'application/pdf') return <FileText className="h-4 w-4 text-red-500" />;
    return <Image className="h-4 w-4 text-blue-500" />;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const renderInfoPanel = () => (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3">My Ticket Statistics</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Open:</span>
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">{stats.open}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">In Progress:</span>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">{stats.in_progress}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">Resolved:</span>
            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">{stats.resolved}</span>
          </div>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2">Quick Tips</h3>
        <ul className="text-sm text-blue-800 space-y-2">
          <li>&#8226; Be specific in your title</li>
          <li>&#8226; Include all relevant details</li>
          <li>&#8226; Mention page/feature name</li>
          <li>&#8226; Describe expected vs actual behavior</li>
        </ul>
      </div>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex gap-2">
          <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-yellow-900 mb-1">Expected Response</h3>
            <p className="text-sm text-yellow-800">
              Urgent: 1-2 hours<br />
              High: 4-6 hours<br />
              Medium: 24 hours<br />
              Low: 48 hours
            </p>
          </div>
        </div>
      </div>
      {recentTickets.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-3">Recent Tickets</h3>
          <div className="space-y-3">
            {recentTickets.map(ticket => (
              <div key={ticket.id} className="border-b border-gray-100 pb-2 last:border-0">
                <p className="text-sm font-medium text-gray-900 truncate">{ticket.title}</p>
                <div className="flex gap-2 mt-1">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityColor(ticket.priority)}`}>
                    {ticket.priority}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Create Support Ticket</h1>
        <p className="text-sm text-gray-500 mt-1">Report an issue to Super Admin</p>
      </div>

      {checkingActive ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : activeTicket ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-8" data-testid="active-ticket-blocker">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertCircle className="h-8 w-8 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-amber-900 mb-2">You Already Have an Active Ticket</h2>
                  <p className="text-amber-800 mb-6">
                    You cannot create a new ticket until your current ticket is resolved. Please wait for a response or check your existing ticket status.
                  </p>
                  
                  <div className="bg-white rounded-lg border border-amber-200 p-5 mb-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-xs text-gray-500 font-mono">{activeTicket.ticket_number}</p>
                        <h3 className="text-lg font-semibold text-gray-900 mt-1">{activeTicket.title}</h3>
                      </div>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                        activeTicket.status === 'open' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {activeTicket.status === 'in_progress' ? 'In Progress' : 'Open'}
                      </span>
                    </div>
                    <div className="flex gap-4 text-sm text-gray-600">
                      {activeTicket.category && (
                        <span>Category: <strong className="capitalize">{activeTicket.category}</strong></span>
                      )}
                      {activeTicket.priority && (
                        <span>Priority: <strong className="capitalize">{activeTicket.priority}</strong></span>
                      )}
                      {activeTicket.created_at && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(activeTicket.created_at)}
                        </span>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(`../tickets/update`)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    data-testid="view-active-ticket-btn"
                  >
                    <ExternalLink className="h-4 w-4" />
                    View My Tickets
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Info Panel - keep it visible */}
          <div className="space-y-4">
            {renderInfoPanel()}
          </div>
        </div>
      ) : (

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Support Policy Notice */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5 mb-6">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-100 rounded-full flex-shrink-0 mt-0.5">
                <AlertCircle className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-blue-900 text-base mb-2">Support & Customization Policy</h3>
                <p className="text-sm text-gray-700 mb-3">Dear Valued CMS Software Users,</p>
                <p className="text-sm text-gray-700 mb-2">
                  Any <strong>issues, breakdowns, or technical problems</strong> related to existing functions and features of the CMS will be resolved by our team <strong>promptly and free of charge</strong>.
                </p>
                <p className="text-sm text-gray-700 mb-2">However, the following requests will be treated as <strong>new customization work</strong> with a separate quotation:</p>
                <ul className="text-sm text-gray-700 ml-4 mb-2 space-y-1 list-disc">
                  <li>Addition of new features or functionalities</li>
                  <li>Modification or removal of existing modules/functions</li>
                  <li>Custom add-ons or enhancements beyond the standard package</li>
                  <li>Any institution-specific customization or development</li>
                </ul>
                <p className="text-sm text-gray-700 mb-2">
                  Customization work will commence only after <strong>approval of the quotation</strong> and receipt of the applicable payment.
                </p>
                <p className="text-xs text-gray-500 mt-3 italic">Best Regards, Ncriptech Team - CMS Software Services</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Ticket Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief description of the issue"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Category */}
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select category</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing / Payment</option>
                    <option value="feature">Feature Request</option>
                    <option value="bug">Bug Report</option>
                    <option value="data">Data Issue</option>
                    <option value="access">Access / Permission</option>
                    <option value="general">General Inquiry</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Please describe the issue in detail. Include steps to reproduce if applicable..."
                  rows="6"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* File Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Attachments <span className="text-gray-400">(Optional - Max 4 files, 150KB each)</span>
                </label>
                
                {/* Upload Button */}
                {attachments.length < MAX_FILES && (
                  <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-gray-600">Add File (PDF, JPG, PNG)</span>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleFileUpload}
                      className="hidden"
                      multiple
                    />
                  </label>
                )}
                
                {/* Attached Files List */}
                {attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-lg border">
                        <div className="flex items-center gap-2">
                          {getFileIcon(file.type)}
                          <span className="text-sm text-gray-700 truncate max-w-[200px]">{file.name}</span>
                          <span className="text-xs text-gray-400">({formatFileSize(file.size)})</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeAttachment(index)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">{attachments.length}/{MAX_FILES} files attached</p>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Plus className="h-5 w-5" />
                {loading ? 'Creating...' : 'Create Ticket'}
              </button>
            </form>
          </div>
        </div>

        {/* Info Panel */}
        <div className="space-y-4">
          {renderInfoPanel()}
        </div>
      </div>
      )}
    </div>
  );
};

export default CreateTicket;
